import * as tslib_1 from "tslib";
import { OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TWO_MINUTES_IN_MILLIS } from '@core/consts';
import { endOfDay, mapId, mapIds, startOfDay } from '@core/utils/filters-utils';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DateIntervalValidation } from '@validators/date-interval-validation';
import { DateValidation } from '@validators/date.validation';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { sentry } from 'sentry';
import { delay } from '../../../core/utils/delay';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { DashboardStorageMovementsMapperService } from './services/dashboard-storage-movements.mapper.service';
import { DashboardStorageMovementsService } from './services/dashboard-storage-movements.service';
var DashboardStorageMovementsComponent = /** @class */ (function () {
    function DashboardStorageMovementsComponent(fuseTranslationLoaderService, fuseProgressBarService, dashboardStorageMovementsService, chartDateIntervalSelectorMapperService) {
        this.fuseTranslationLoaderService = fuseTranslationLoaderService;
        this.fuseProgressBarService = fuseProgressBarService;
        this.dashboardStorageMovementsService = dashboardStorageMovementsService;
        this.chartDateIntervalSelectorMapperService = chartDateIntervalSelectorMapperService;
        this.isLoading = true;
        this.isFilterOn = false;
        this.totalMaintenances = 0;
        this.isFilterExpanded = false;
        this.accountMaintenancesStatusInterval = {
            responseAverage: '',
            solutionAverage: '',
            pending: '',
            scheduled: '',
            inProgress: '',
            reported: '',
            requested: '',
            onNegotiation: '',
        };
        this.movementsByType = {
            entryCount: 0,
            removeCount: 0,
            transferCount: 0,
        };
        this.subscriptions = new Subject();
        this.offset = 0;
        this.limit = 100;
        this.hasAccountStorageMovementsByDayData = false;
        this.defaultFilterValue = {
            from: moment().subtract(1, 'month'),
            to: moment(),
            product: null,
            storages: null,
        };
        this.filterForm = new FormGroup({
            from: new FormControl(this.defaultFilterValue.from, [Validators.required, DateValidation.validate]),
            to: new FormControl(this.defaultFilterValue.to, [Validators.required, DateValidation.validate]),
            product: new FormControl(this.defaultFilterValue.product),
            storages: new FormControl(this.defaultFilterValue.storages),
        }, {
            validators: [
                DateIntervalValidation.validate({
                    from: 'from',
                    to: 'to',
                    maxDiffLte: 1,
                    unit: 'year'
                }),
            ]
        });
        this.productByStorage = [];
        this.movementsByDay = [];
        this.movementsByDayTimeline = [];
        this.movements = [];
        this.fuseTranslationLoaderService.loadJsonTranslations({ english: english, portuguese: portuguese, spanish: spanish });
    }
    DashboardStorageMovementsComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.updateAutoRefreshState({ enabled: false });
        this.filterForm.valueChanges.pipe(takeUntil(this.subscriptions), filter(function () { return _this.filterForm.valid; }), debounceTime(200), tap(function () { return _this.showLoading(); }), tap(function () {
            var currentWhere = _this.toQuery(_this.filterForm.value);
            var defaultWhere = _this.toQuery(_this.defaultFilterValue);
            _this.isFilterOn = !isEqual(currentWhere, defaultWhere);
        }), switchMap(function () { return _this.updateData(); }), tap(function () { return _this.checkIfThereIsData(); }), tap(function () { return _this.hideLoading(); })).subscribe();
        this.reload();
        this.checkIfThereIsData();
    };
    DashboardStorageMovementsComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.next();
        this.subscriptions.complete();
        clearInterval(this.intervalId);
    };
    DashboardStorageMovementsComponent.prototype.toQuery = function (value) {
        return {
            from: startOfDay(value.from),
            to: endOfDay(value.to),
            productId: mapId(value.product),
            storageId_in: mapIds(value.storages),
        };
    };
    DashboardStorageMovementsComponent.prototype.updateData = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var where, _a, movementsByType, productByStorage, movementsByDay, movements, maintenanceCompletedTimeline, err_1;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        where = this.toQuery(this.filterForm.value);
                        return [4 /*yield*/, Promise.all([
                                this.dashboardStorageMovementsService.accountStorageMovementsByType(where),
                                where.productId && this.dashboardStorageMovementsService.accountProductsByStorage(where),
                                where.productId && this.dashboardStorageMovementsService.accountStorageMovementsByDay(where),
                                where.productId && this.dashboardStorageMovementsService.storageMovements(where, this.limit, this.offset),
                                delay(700)
                            ])];
                    case 1:
                        _a = tslib_1.__read.apply(void 0, [_b.sent(), 4]), movementsByType = _a[0], productByStorage = _a[1], movementsByDay = _a[2], movements = _a[3];
                        this.movementsByType = movementsByType;
                        this.productByStorage = productByStorage;
                        this.movementsByDay = movementsByDay;
                        this.movements = movements;
                        maintenanceCompletedTimeline = this.chartDateIntervalSelectorMapperService.toLineChartFormat({
                            movementsByDay: movementsByDay,
                            from: where.from,
                            to: where.to,
                        });
                        this.movementsByDayTimeline = maintenanceCompletedTimeline;
                        return [3 /*break*/, 3];
                    case 2:
                        err_1 = _b.sent();
                        console.error(err_1);
                        sentry.captureException(err_1);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    DashboardStorageMovementsComponent.prototype.updateAutoRefreshState = function (_a) {
        var _this = this;
        var enabled = _a.enabled;
        if (enabled) {
            this.intervalId = setInterval(function () { return _this.reload(); }, TWO_MINUTES_IN_MILLIS);
        }
        else {
            clearInterval(this.intervalId);
        }
    };
    DashboardStorageMovementsComponent.prototype.clearFilters = function () {
        this.filterForm.setValue(this.defaultFilterValue);
    };
    DashboardStorageMovementsComponent.prototype.showLoading = function () {
        this.isLoading = true;
        this.fuseProgressBarService.show();
    };
    DashboardStorageMovementsComponent.prototype.hideLoading = function () {
        this.isLoading = false;
        this.fuseProgressBarService.hide();
    };
    DashboardStorageMovementsComponent.prototype.reload = function () {
        this.filterForm.updateValueAndValidity();
    };
    DashboardStorageMovementsComponent.prototype.beforePrint = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                this.isFilterExpanded = true;
                return [2 /*return*/];
            });
        });
    };
    DashboardStorageMovementsComponent.prototype.checkIfThereIsData = function () {
        this.hasAccountStorageMovementsByDayData = this.movementsByDay && this.movementsByDay.some(function (item) { return item.finalCount; });
    };
    return DashboardStorageMovementsComponent;
}());
export { DashboardStorageMovementsComponent };
